import { Helmet } from 'react-helmet-async';
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { GraduationCap, Euro, Shield, Phone, Calendar } from "lucide-react";
import { openCalendlyPopup } from '@/utils/calendly';
import WhatsAppButton from '@/components/WhatsAppButton';
const Studenten = () => {
  return <div className="min-h-screen bg-background">
      <Helmet>
        <title>Studentenverzekeringen - Compleet Verzekerd</title>
        <meta name="description" content="Studentenverzekeringen zonder kosten tijdens studie. Scherpe premies, geen verplichtingen, flexibel opzegbaar. Perfect voor je studentenperiode!" />
        <meta name="keywords" content="studentenverzekeringen, verzekeringen studenten, geen kosten tijdens studie, studentenkorting, flexibele verzekeringen, goedkope autoverzekering student" />
        <link rel="canonical" href="https://compleetverzekerd.nl/studenten/" />
      </Helmet>
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 bg-gradient-to-br from-primary/10 to-accent/10">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <GraduationCap className="w-16 h-16 text-primary mx-auto mb-6" />
            <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6">Speciaal voor studenten</h1>
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">Als je studeert telt vaak elke euro. Maar ook als student heb je verzekeringen nodig. Bij Compleet Verzekerd betaal je geen kosten voor onze dienstverlening zolang je studeert!</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" onClick={openCalendlyPopup} className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-3">
                <Calendar className="h-5 w-5" />
                Plan een afspraak
              </Button>
              <Button variant="outline" size="lg" asChild>
                <a href="tel:0782200021">
                  <Phone className="h-5 w-5" />
                  078 - 220 00 21
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <Card className="text-center">
              <CardHeader>
                <Euro className="h-12 w-12 text-primary mx-auto mb-4" />
                <CardTitle>Scherpe premies</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Bijzonder scherpe premies voor fiets-, auto-, inboedel-, aansprakelijkheids- en reisverzekeringen.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardHeader>
                <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
                <CardTitle>Geen kosten tijdens studie</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">Helemaal geen kosten voor onze dienstverlening zolang je studeert. Bespaar tientjes tot honderden euro's per jaar!</p>
              </CardContent>
            </Card>

            <Card className="text-center">
              <CardHeader>
                <GraduationCap className="h-12 w-12 text-primary mx-auto mb-4" />
                <CardTitle>Geen verplichtingen</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Je zit nergens aan vast. Na je studie kun je gewoon blijven of elders verzekeren. Even goede vrienden!
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 bg-muted/50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto bg-background rounded-lg p-8">
            <h2 className="text-3xl font-bold text-center mb-8">Geen kosten? Hoe werkt dat?</h2>
            <div className="text-muted-foreground leading-relaxed">
              <p className="mb-6">
                Elke verzekeraar maakt kosten voor het distribueren van verzekeringen. Als de verzekeraar 
                zelf de distributie verzorgt (de zogenaamde direct writers zoals Ohra, Centraal Beheer en 
                FBTO), worden deze kosten opgenomen in de premie die je betaalt voor de verzekering.
              </p>
              <p className="mb-6">
                Bij veel verzekeraars worden de verzekeringen gedistribueerd via een tussenpersoon 
                (intermediair). Die ontvangt daarvoor een provisie van de verzekeraar of hij brengt een 
                vergoeding in rekening direct bij de klant via een service abonnement. Bij Compleet 
                Verzekerd hanteren wij beide varianten, afhankelijk van wat beter uitpakt voor onze klant.
              </p>
              <p className="mb-6">
                Als iemand één of twee verzekeringen afsluit is een ingebouwde provisie vaak voordeliger. 
                Gaat het om meer verzekeringen, dan is een service-abonnement al snel aantrekkelijker.
              </p>
              <p className="font-semibold text-primary">
                Maar met jou spreken we af dat we helemaal geen kosten in rekening brengen. Geen 
                provisie, geen service abonnement, helemaal niets! Maar je krijgt wel de dienstverlening 
                die al onze klanten van ons krijgen. Goeie deal dus!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* After graduation */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-6">
          <Card className="max-w-4xl mx-auto">
            <CardHeader>
              <CardTitle className="text-2xl">Als je klaar bent met studeren</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground leading-relaxed">
                Uiteraard hopen wij dat je zo tevreden bent met onze dienstverlening, dat je klant bij ons 
                blijft nadat je klaar bent met studeren. Maar je zit nergens aan vast. Mocht je besluiten om 
                tegen die tijd toch je verzekeringen elders onder te brengen, even goede vrienden!
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Meer weten?</h2>
            <p className="text-lg mb-12 max-w-3xl mx-auto leading-relaxed opacity-90">Je kunt een afspraak inplannen via de website of even bellen met een van onze medewerkers. Je krijgt geen keuze menu maar gewoon contact met een adviseur of assistent. De afspraak kan plaatsvinden bij ons op kantoor of online via een videogesprek. Een gesprek duurt meestal maar 15 tot 30 minuten. Er worden geen kosten in rekening gebracht voor een adviesgesprek. In veel gevallen is een afspraak niet eens nodig en kunnen we je direct telefonisch al verder helpen.</p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="secondary" size="lg" className="bg-background text-foreground hover:bg-background/90" onClick={openCalendlyPopup}>
                <Calendar className="h-5 w-5" />
                Plan een afspraak
              </Button>
              <Button variant="outline-inverse" size="lg" asChild>
                <a href="tel:0782200021">
                  <Phone className="h-5 w-5" />
                  078 - 220 00 21
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>;
};
export default Studenten;